import { DownArrowIcon } from "@xola/icons";
import { Modal, Popover, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { Field, FieldProps } from "formik";
import React, { ReactNode, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Experience, PreferenceKey } from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { Loader } from "../Loader";
import { api } from "@/api/api";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { findByKey } from "@/models/Preference";
import { DashboardColorPreference } from "@/types/Dashboard";

interface CustomizeDashboardModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CustomizeDashboardModal = ({ isOpen, onClose }: CustomizeDashboardModalProps) => {
    return (
        <Modal isOpen={isOpen} size="large" onClose={onClose}>
            <ModalContent onClose={onClose} />
        </Modal>
    );
};

const initialValues = {
    key: PreferenceKey.XOLA_SELLER_DASHBOARD_COLORS,
    value: { background: "none", sidebar: "availability" },
};

interface ModalContentProps {
    onClose: () => void;
}

const ModalContent = ({ onClose }: ModalContentProps) => {
    const { data: experiences, isLoading: isLoadingExperiences } = useAutoFetchCollection<Experience>("experiences");

    const { data: preferences = [], isLoading: isLoadingPreferences } =
        useInfiniteCollection<DashboardColorPreference>("preferences");

    const queryClient = useQueryClient();
    const seller = useSeller();

    // Custom hook used for persisting resources on the server.
    // Invalidates the cache after successful calls.
    const { isLoading: isSaving, mutate: savePreference } = useMutation(
        "preferences",
        async (preference: DashboardColorPreference) => {
            const promise = preference?.id
                ? api.patch(`/preferences/${preference.id}`, preference)
                : api.post(`/preferences`, { ...preference, seller: { id: seller.id } });
            await promise;

            queryClient.invalidateQueries("preferences");
            onClose();
        },
    );

    // Find dashboard preference in preferences collection.
    const currentPreference = findByKey(preferences, PreferenceKey.XOLA_SELLER_DASHBOARD_COLORS);

    const handleSubmit = (preference: any) => {
        savePreference(preference);
    };

    if (isLoadingExperiences || isLoadingPreferences) {
        return (
            <Modal.Body>
                <Loader />
            </Modal.Body>
        );
    }

    return (
        <XolaFormik enableReinitialize initialValues={currentPreference ?? initialValues} onSubmit={handleSubmit}>
            {({ handleSubmit, values }) => {
                const isColorMenuDisabled = values.value.sidebar !== "listing" && values.value.background !== "listing";

                return (
                    <>
                        <Modal.Header>Customize Dashboard Appearance</Modal.Header>

                        <Modal.Body>
                            <fieldset className="space-y-8" disabled={isSaving}>
                                <Label label="Background Color">
                                    <div className="space-x-4">
                                        <label>
                                            <Field
                                                className="mr-1"
                                                type="radio"
                                                name="value.background"
                                                value="listing"
                                            />
                                            Product Color
                                        </label>

                                        <label>
                                            <Field
                                                className="mr-1"
                                                type="radio"
                                                name="value.background"
                                                value="availability"
                                            />
                                            Availability
                                        </label>

                                        <label>
                                            <Field className="mr-1" type="radio" name="value.background" value="none" />
                                            None
                                        </label>
                                    </div>
                                </Label>

                                <Label label="Sidebar Color">
                                    <div className="space-x-4">
                                        <label>
                                            <Field className="mr-1" type="radio" name="value.sidebar" value="listing" />
                                            Product Color
                                        </label>

                                        <label>
                                            <Field
                                                className="mr-1"
                                                type="radio"
                                                name="value.sidebar"
                                                value="availability"
                                            />
                                            Availability
                                        </label>
                                    </div>
                                </Label>

                                <Label label="Product Colors">
                                    <div className="max-h-64 overflow-y-auto p-1 xl:max-h-80 2xl:max-h-96">
                                        {experiences.map((experience) => (
                                            <div
                                                key={experience.id}
                                                className="mb-4 flex items-center"
                                                data-testid="customize-dashboard-modal-experience-item"
                                            >
                                                <Field name={`value.experiences.${experience.id}`}>
                                                    {({ field, form }: FieldProps) => (
                                                        <ColorMenu
                                                            isDisabled={isColorMenuDisabled}
                                                            className="mr-4 w-16"
                                                            value={field.value ?? null}
                                                            onChange={(color) => {
                                                                const key = `value.experiences.${experience.id}`;
                                                                form.setFieldValue(key, color);
                                                            }}
                                                        />
                                                    )}
                                                </Field>

                                                <NoTranslate className={clsx(isColorMenuDisabled && "text-gray")}>
                                                    {experience.name}
                                                </NoTranslate>
                                            </div>
                                        ))}
                                    </div>
                                </Label>
                            </fieldset>
                        </Modal.Body>

                        <Modal.Footer className="space-x-4">
                            <SecondaryButton disabled={isSaving} onClick={onClose}>
                                Cancel
                            </SecondaryButton>

                            <SubmitButton type="button" isLoading={isSaving} onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface LabelProps {
    label: string;
    children: ReactNode;
}

const Label = ({ label, children }: LabelProps) => {
    return (
        <div className="flex">
            <div className="w-44 font-bold">{label}</div>
            <div className="flex-1">{children}</div>
        </div>
    );
};

interface ColorMenuProps {
    isDisabled?: boolean;
    className?: string;
    value: string | null;
    onChange: (value: string | null) => void;
}

const ColorMenu = ({ isDisabled = false, className, value = null, onChange }: ColorMenuProps) => {
    const [showMenu, setShowMenu] = useState(false);

    return (
        <Popover
            maxWidth={300}
            placement="auto" // For when you have too many listings, popper can adjust
            appendTo={document.body}
            zIndex={40}
            visible={showMenu}
            onClickOutside={() => setShowMenu(false)}
        >
            <SecondaryButton disabled={isDisabled} size="small" className={className} onClick={() => setShowMenu(true)}>
                <Color className="mr-1 flex-shrink-0" value={value} size="small" />
                <DownArrowIcon className="flex-shrink-0" size="medium" />
            </SecondaryButton>

            <Popover.Content className="w-20 divide-y-2 divide-gray-lighter">
                {colors.map((color, index) => (
                    <div
                        key={color}
                        data-testid="color-menu-item"
                        role="button"
                        className={clsx(
                            "cursor-pointer py-2 text-center leading-0 hover:bg-gray-lighter",
                            index === 0 && "rounded-t",
                            index + 1 === colors.length && "rounded-b",
                        )}
                        onClick={() => {
                            onChange(color);
                            setShowMenu(false);
                        }}
                    >
                        <Color value={color} />
                    </div>
                ))}
            </Popover.Content>
        </Popover>
    );
};

const colorSizes = {
    small: "w-2.5 h-2.5",
    medium: "w-6 h-6",
};

interface ColorProps {
    size?: keyof typeof colorSizes;
    value: string | null;
    className?: string;
}

export const Color = ({ size = "medium", value = null, className }: ColorProps) => {
    return (
        <div
            data-testid="color"
            className={clsx(
                colorSizes[size],
                className,
                "inline-block rounded-full",
                value ? null : "border-2 border-gray",
            )}
            style={{ backgroundColor: value ?? "white" }}
        />
    );
};

const colors = [
    null,
    "#ff95b5",
    "#ff1c60",
    "#ffa788",
    "#ff6935",
    "#ffd874",
    "#f8b200",
    "#80e220",
    "#06b452",
    "#1ea5fc",
    "#004bdc",
    "#b479ff",
    "#7328d3",
];
